import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { apiClient, ApiClient, safeLocalStorageSet } from './api-client';

// Upgrade http:// → https:// — .env.local is loaded by Vite in all modes
// including production builds, so a dev http:// address must be sanitized here.
// localhost/127.0.0.1 stay http: they have no TLS and can't be mixed content.
const rawApiUrl = import.meta.env.VITE_API_URL || "";
const API_URL = /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?/.test(rawApiUrl)
  ? rawApiUrl
  : rawApiUrl.replace(/^http:\/\//, "https://");

interface User {
  id: string;
  recipeAppAccessId?: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  isAdmin?: boolean;
  profileImg?: string;
  currency?: string;
  timezone?: string;
  unitSystem?: string;
  username?: string;
  businessName?: string;
  permissions?: Record<string, boolean>;
  subscriptionPermissions?: Record<string, boolean>;
  isAccountOwner?: boolean;
  subscriptionStatus?: string;
  subscriptionEndDate?: string;
  trialEndsAt?: string;
  onboardingCompleted?: boolean;
  subscriptionPlanId?: string;
  subscriptionPlanName?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ subscriptionExpired: boolean }>;
  register: (
    userName: string,
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    gender: string,
    birthday: string,
    age: string,
    userType: string,
    referralSource?: string
  ) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    // A stale logging_in flag from a prior tab that was closed mid-login suppresses
    // 401 redirects. Clear it unconditionally on mount — if this IS an active login
    // the flag will be re-set immediately when login() is called.
    localStorage.removeItem('logging_in');

    // Check for existing token on mount
    const token = ApiClient.getToken();
    const savedUser = localStorage.getItem('auth_user');
    
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);

        // Impersonation sessions: trust the JWT payload written by useImpersonation.
        // Skip /me refresh — the token is already server-signed and short-lived.
        if (parsedUser.isImpersonation) {
          setIsLoading(false);
          return;
        }

        // Regular sessions: always fetch fresh data to pick up subscription/status changes
        fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : Promise.reject(r.status)).then((freshUser: any) => {
          if (ApiClient.getToken() !== token) return; // a newer session took over mid-flight
          const normalized = normalizeUserData(freshUser);
          safeLocalStorageSet('auth_user', JSON.stringify(normalized));
          setUser(normalized);
        }).catch((statusOrError: unknown) => {
          // Only sign out on an explicit server-side auth rejection (401/403).
          // Network errors (TypeError, timeout, DNS failure) resolve to a non-numeric
          // value — keep the cached user so the app stays usable on flaky mobile
          // connections. Subsequent authenticated requests will 401 if the token
          // has actually expired, and that will trigger the normal sign-out path.
          if (statusOrError === 401 || statusOrError === 403) {
            // Race guard: if the user logged in again while this stale-token /me
            // was in flight (e.g. they were sitting on /signin with an expired
            // session and signed in before the 401 landed), the current token is
            // no longer the one that failed — do NOT clobber the fresh session.
            if (ApiClient.getToken() !== token) return;
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('current-workspace-id');
            localStorage.removeItem('workspaces');
            localStorage.removeItem('subscription_expired');
            localStorage.removeItem('logging_in');
            apiClient.clearToken();
            setUser(null);
            // Redirect immediately — ProtectedLayout already passed its check
            // (checked=true) so it won't re-run the redirect logic on its own.
            setLocation('/signin');
          }
          // else: network error — retain the stale cached user
        });
      } catch (error) {
        console.error('Failed to parse saved user:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('current-workspace-id');
        localStorage.removeItem('workspaces');
        localStorage.removeItem('subscription_expired');
        localStorage.removeItem('logging_in');
        apiClient.clearToken();
      }
    } else if (token && !savedUser) {
      // Token exists (likely from cookie after mobile Safari evicted localStorage)
      // but auth_user is missing. Recover the session by fetching /me with the token.
      fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : Promise.reject(r.status)).then((freshUser: any) => {
        if (ApiClient.getToken() !== token) return; // a newer session took over mid-flight
        const normalized = normalizeUserData(freshUser);
        safeLocalStorageSet('auth_user', JSON.stringify(normalized));
        setUser(normalized);
      }).catch((statusOrError: unknown) => {
        // Race guard: a fresh login while this stale-token recovery was in
        // flight must not be clobbered by the old token's rejection.
        if (ApiClient.getToken() !== token) return;
        // Token is invalid — clear everything and redirect to sign in
        localStorage.removeItem('auth_user');
        localStorage.removeItem('current-workspace-id');
        localStorage.removeItem('workspaces');
        localStorage.removeItem('subscription_expired');
        localStorage.removeItem('logging_in');
        apiClient.clearToken();
        localStorage.removeItem('auth_token');
        setUser(null);
        setLocation('/signin');
      }).finally(() => {
        setIsLoading(false);
      });
      // isLoading stays true until the fetch settles — ProtectedLayout
      // will render null during this window instead of redirecting.
      return;
    }
    setIsLoading(false);

    // Detect token changes from other tabs (e.g. another tester logs in/out)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token') {
        if (!e.newValue) {
          // Token removed in another tab — log out this tab too
          localStorage.removeItem('logging_in');
          setUser(null);
          setLocation('/signin');
        } else if (e.newValue !== e.oldValue) {
          // Different token — different user logged in, reload to sync
          window.location.reload();
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Helper to normalize user data from backend
  const normalizeUserData = (userData: any): User => {
    return {
      ...userData,
      username: userData.userName || userData.username, // Map userName to username
    };
  };

  const login = async (email: string, password: string) => {
    safeLocalStorageSet('logging_in', 'true');
    try {
      const data = await apiClient.login(email, password);
      const normalizedUser = normalizeUserData(data.user);

      // Write token first (apiClient.login already does this), then user.
      // Re-verify both are present before updating state (mobile Safari timing fix).
      let attempts = 0;
      while (attempts < 5) {
        const token = ApiClient.getToken();
        if (token) break;
        await new Promise(r => setTimeout(r, 50));
        attempts++;
      }

      safeLocalStorageSet('auth_user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);

      const savedToken = ApiClient.getToken();
      if (!savedToken) throw new Error('Failed to save authentication token');

      // Clear any stale workspace keys from a previous session so the new
      // session's queries don't fire with a workspace ID that belongs to a
      // different account (which would return 401 and trigger a sign-out loop).
      localStorage.removeItem('current-workspace-id');
      localStorage.removeItem('workspaces');
      localStorage.removeItem('subscription_expired');

      // Login now SUCCEEDS for expired subscriptions (2026-08-31) — the server
      // flags it so the client can land the user on the plans/checkout page
      // (the only authenticated surface an expired account can use). Seed the
      // same flag the 403 handlers use so SubscriptionPlansPublic shows its
      // expired banner.
      const subscriptionExpired = !!(data as any).subscriptionExpired;
      if (subscriptionExpired) safeLocalStorageSet('subscription_expired', 'true');

      // Clear flag after a delay to let initial workspace + data queries settle.
      setTimeout(() => localStorage.removeItem('logging_in'), 10000);
      return { subscriptionExpired };
    } catch (error) {
      // Always clear the flag on failure — leaving it set would suppress 401
      // redirects for up to 10 seconds after a bad-password or network error.
      localStorage.removeItem('logging_in');
      throw error;
    }
  };

  const register = async (
    userName: string,
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    gender: string,
    birthday: string,
    age: string,
    userType: string,
    referralSource?: string
  ) => {
    await apiClient.register(email, password, firstName, lastName, userName, gender, birthday, age, userType, referralSource);
    // After registration, log in
    await login(email, password);
  };

  const logout = () => {
    // Set flag to prevent 401 redirects during logout
    safeLocalStorageSet('logging_out', 'true');
    
    apiClient.clearToken();
    localStorage.removeItem('auth_user');
    // Workspace cache keys actually used by WorkspaceContext + getAuthHeaders.
    // Stale entries from a prior session would otherwise leak into the next
    // sign-in and silently send the wrong x-workspace-id on early queries.
    localStorage.removeItem('current-workspace-id');
    localStorage.removeItem('workspaces');
    localStorage.removeItem('subscription_expired'); // Don't leak expired-sub flag to the next session
    localStorage.removeItem('logging_in'); // Clear any stuck login flag
    queryClient.clear(); // Clear all React Query cache
    setUser(null);
    
    // Remove flag and redirect
    setTimeout(() => {
      localStorage.removeItem('logging_out');
      setLocation('/signin');
    }, 100);
  };

  const refreshUser = async () => {
    try {
      const token = ApiClient.getToken();
      if (!token) return;

      const url = `${import.meta.env.VITE_API_URL || ''}/api/auth/me`;

      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

      if (response.ok) {
        if (ApiClient.getToken() !== token) return; // a newer session took over mid-flight
        const userData = await response.json();
        const normalizedUser = normalizeUserData(userData);
        setUser(normalizedUser);
        safeLocalStorageSet('auth_user', JSON.stringify(normalizedUser));
      } else if (response.status === 401 || response.status === 403) {
        // Race guard: don't clobber a session that was re-established while
        // this request was in flight (see the /me init fetch above).
        if (ApiClient.getToken() !== token) return;
        // Token has expired or been revoked — sign out cleanly rather than
        // leaving a stale user in state with no valid session.
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('current-workspace-id');
        localStorage.removeItem('workspaces');
        localStorage.removeItem('subscription_expired');
        localStorage.removeItem('logging_in');
        apiClient.clearToken();
        setUser(null);
        setLocation('/signin');
      }
      // 5xx / network errors: keep current user state intact (same principle as
      // the /me init fetch — don't sign out on server/network problems)
    } catch {
      // Network error — retain current user, do nothing
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
